import { DeliveryFileDetails } from './DeliveryFileDetails';
import { TabMethods } from './models';
import { ContactDetails, ContactPicker, FormLayout } from '../common';
import { showOverrideContactWarning } from '../common/Utils';
import { objectUtils } from '@/app/core/utils';
import { FileNamesTemplate } from '@/app/features/products';
import { useDataStore } from '@/app/core/components/step-wizard';
import {
  forwardRef,
  useImperativeHandle,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import {
  OutputLocation,
  OutputTargetParty,
  ClientAttributes,
  asContact,
  ClientContact,
  outputLocationValidator,
  FieldDelimiter,
  Contact,
  MaxAttributesForDelimiterSelection,
} from '@ssar-app/domain';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  Grid,
  GridItem,
  Text,
  Divider,
} from '@chakra-ui/react';
import { FormLabel, RadioGroup, Select } from '@ssar-app/uikit';
import { upperCase } from 'lodash';

interface OutputLocationTabProps {
  model?: OutputLocation;
  fileNamesTemplate: FileNamesTemplate;
  onFileSelectionChanged: (outputLocation: OutputLocation) => void;
  onContactSelected: (outputLocation: OutputLocation) => void;
  clientAttributes?: ClientAttributes;
  onOutputContactSelected?: () => void;
  onChange?: (outputLocation: OutputLocation) => void;
}

interface PhysicalAddressType {
  target: string;
  clientLocation: string;
  clientContact: Contact;
  tpLocation: string;
  tpContact: Contact;
}

const OutputLocationTab: React.ForwardRefRenderFunction<
  TabMethods,
  OutputLocationTabProps
> = (props, ref) => {
  const formMethods = useForm<
    OutputLocation & {
      outputFiles: string;
    }
  >({
    defaultValues: props.model,
    mode: 'onChange',
    resolver: yupResolver(outputLocationValidator),
  });
  const { errors, control, register, clearErrors, watch, setValue, getValues } =
    formMethods;
  const { data } = useDataStore();
  const [selectedDelimiter, setSelectedDelimiter] = useState<string>(
    props?.model?.delimiter || FieldDelimiter.COMMA,
  );

  const { onOutputContactSelected, onChange } = props;
  const reachedLimitForFixedDelimiter =
    data?.attributesAndBundles?.attributes?.length >
    MaxAttributesForDelimiterSelection;

  const DelimterOptions = useMemo(
    () =>
      Object.entries(FieldDelimiter).map(([value, label]) => ({
        label: label,
        value: value,
        disabled:
          reachedLimitForFixedDelimiter && label !== FieldDelimiter.FIXED,
      })),
    [reachedLimitForFixedDelimiter],
  );

  useEffect(() => {
    if (reachedLimitForFixedDelimiter) {
      setValue('delimiter', FieldDelimiter.FIXED);
    } else {
      setValue('delimiter', props.model?.delimiter || FieldDelimiter.COMMA);
    }
  }, [reachedLimitForFixedDelimiter, props.model?.delimiter, setValue]);

  useEffect(() => {
    register('outputFiles');
    if (props.model?.contact)
      updatePhysicalAddress(
        true,
        props.model?.target ? props.model?.target : OutputTargetParty.Client,
        props.model?.location,
        props.model?.contact,
      );
    else
      updatePhysicalAddress(
        false,
        props.model?.target ? props.model?.target : OutputTargetParty.Client,
        '',
        defaultPhyAddr.clientContact,
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(ref, () => {
    return {
      getTabData: formMethods.getValues,
      isValid: () => {
        return formMethods
          .trigger()
          .then((a) => {
            return a;
          })
          .catch((err) => {
            console.error(err);
            return false;
          });
      },

      submit: () => {
        return formMethods
          .trigger()
          .then((a) => {
            return a;
          })
          .catch((err) => {
            console.error(err);
            return false;
          });
      },
    };
  });

  const defaultPhyAddr: PhysicalAddressType = {
    target: OutputTargetParty.Client,
    clientLocation: '',
    clientContact: {
      name: '',
      clientName: '',
      email: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      phoneNumber: '',
    },
    tpLocation: '',
    tpContact: {
      name: '',
      clientName: '',
      email: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      phoneNumber: '',
    },
  };

  const destinationTarget = watch('target');
  const [physicalAddress, setPhysicalAddress] = useState<PhysicalAddressType>({
    ...defaultPhyAddr,
  });

  const updateContactDetails = useCallback((): void => {
    const newContact: Contact =
      physicalAddress.target === OutputTargetParty.Client
        ? physicalAddress.clientContact
        : physicalAddress.tpContact;
    const newLocation: string =
      physicalAddress.target === OutputTargetParty.Client
        ? physicalAddress.clientLocation
        : physicalAddress.tpLocation;
    const cloneContact = { ...newContact }; // cloning contact to not override it because of the delivery CLient/Third Party change.
    setValue('location', newLocation || '', {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue('contact', cloneContact, {
      shouldDirty: true,
      shouldValidate: true,
    });
    props.onContactSelected(getValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [physicalAddress]);

  useEffect(() => {
    updateContactDetails();
  }, [updateContactDetails]);

  const updatePhysicalAddress = (
    updateAddress: boolean,
    target: string,
    location: string,
    contact: Contact,
  ): void => {
    if (updateAddress) {
      if (target === OutputTargetParty.Client) {
        setPhysicalAddress((prevState) => ({
          ...prevState,
          target: target,
          clientLocation: location,
          clientContact: contact,
        }));
      } else {
        setPhysicalAddress((prevState) => ({
          ...prevState,
          target: target,
          tpLocation: location,
          tpContact: contact,
        }));
      }
    } else {
      setPhysicalAddress((prevState) => ({ ...prevState, target: target }));
    }
  };

  const updateContact = (newClientContact: ClientContact): void => {
    updatePhysicalAddress(
      true,
      physicalAddress.target,
      newClientContact.stsFolderId,
      asContact(newClientContact),
    );
  };

  const contactSelected = (newClientContact: ClientContact): void => {
    const newContact = asContact(newClientContact);

    const { contact: existingContact } = getValues();
    // console.log('existingContact:', existingContact);
    if (existingContact && objectUtils.areAllPropsEmpty(existingContact)) {
      updateContact(newClientContact);
    } else if (!objectUtils.areEqual(existingContact, newContact, '_id')) {
      showOverrideContactWarning(newClientContact, (clientContact) => {
        updateContact(clientContact);
      });
    } else {
      updateContact(newClientContact);
    }
  };

  useEffect(() => {
    onChange && onChange(getValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    onChange && onChange(getValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDelimiter]);

  return (
    <form>
      <FormLayout header={'Output File Information'}>
        <Flex direction='column' w={'full'}>
          <FormControl isRequired isInvalid={!!errors.delimiter} mb={8}>
            <Grid templateColumns='repeat(6,1fr)'>
              <GridItem display='flex' justifyContent='end'>
                <FormLabel
                  fontSize='sm'
                  htmlFor='delimiter'
                  mb={0}
                  mr={2}
                  marginTop={1}
                >
                  Delimiter :
                </FormLabel>
              </GridItem>
              <GridItem colSpan={3}>
                <Controller
                  name='delimiter'
                  control={control}
                  defaultValue={
                    reachedLimitForFixedDelimiter
                      ? FieldDelimiter.FIXED
                      : props.model?.delimiter || FieldDelimiter.COMMA
                  }
                  render={({ value, onChange }) => (
                    <Select
                      defaultValue={value}
                      selectContainerProps={{ width: '250px' }}
                      options={DelimterOptions}
                      onChange={(value) => {
                        onChange(value);
                        setValue('delimiter', value as string);
                        setSelectedDelimiter(value as string);
                      }}
                    ></Select>
                  )}
                />
                <FormErrorMessage>
                  {errors?.delimiter?.message}
                </FormErrorMessage>
                <FormHelperText>
                  {reachedLimitForFixedDelimiter ? (
                    <Text colorScheme='secondary' fontSize='small'>
                      {`Note: Delimiter(s) other then "${FieldDelimiter.FIXED}" cannot be used as the selected attributes are more than ${MaxAttributesForDelimiterSelection}.`}
                    </Text>
                  ) : (
                    ''
                  )}
                </FormHelperText>
              </GridItem>
            </Grid>
          </FormControl>
          <Divider color='gray.300' my={4} mt={-2} />
          <Text mb={4}>
            Output Contents: Select all that will be sent to the output location
            {errors?.outputFiles?.message && (
              <Text color='red'>{errors?.outputFiles?.message}</Text>
            )}
          </Text>
          <DeliveryFileDetails
            defaultFileName={props.fileNamesTemplate.mainFileName}
            onFileSelectionChanged={props.onFileSelectionChanged}
            clearErrors={clearErrors}
            label='Main File'
            propName='mainFile.'
            control={formMethods.control}
            errors={errors?.mainFile}
            model={props.model?.mainFile}
            setValue={setValue}
            getValues={getValues}
          />
          <DeliveryFileDetails
            defaultFileName={props.fileNamesTemplate.rejectFileName}
            onFileSelectionChanged={props.onFileSelectionChanged}
            clearErrors={clearErrors}
            label='Reject File'
            propName='rejectFile.'
            control={formMethods.control}
            errors={errors?.rejectFile}
            model={props.model?.rejectFile}
            setValue={setValue}
            getValues={getValues}
          />
          <DeliveryFileDetails
            defaultFileName={props.fileNamesTemplate.statementFileName}
            onFileSelectionChanged={props.onFileSelectionChanged}
            clearErrors={clearErrors}
            label='Consumer Statement File'
            propName='statementFile.'
            control={formMethods.control}
            errors={errors?.statementFile}
            model={props.model?.statementFile}
            setValue={setValue}
            getValues={getValues}
          />
          <Text mt={8}>
            Output Zip Option: All output files will be zipped if selected
          </Text>
          <DeliveryFileDetails
            defaultFileName={props.fileNamesTemplate.zippedFileName}
            onFileSelectionChanged={props.onFileSelectionChanged}
            clearErrors={clearErrors}
            label='Zipped File'
            propName='zippedFile.'
            control={formMethods.control}
            errors={errors?.zippedFile}
            model={props.model?.zippedFile}
            setValue={setValue}
            getValues={getValues}
          />
        </Flex>
      </FormLayout>
      <FormLayout header={'Output Shipment Information'}>
        <FormControl isRequired isInvalid={!!errors.target}>
          <Grid templateColumns='repeat(6,1fr)'>
            <GridItem justifyContent='end' display='flex'>
              <FormLabel
                fontSize='sm'
                htmlFor='target'
                mb={0}
                mr={2}
                marginTop={1}
              >
                Deliver to :
              </FormLabel>
            </GridItem>
            <GridItem colSpan={3}>
              <Controller
                name='target'
                control={control}
                defaultValue={props.model?.target || OutputTargetParty.Client}
                render={({ onChange, value }) => {
                  return (
                    <RadioGroup
                      variant='solid'
                      size='lg'
                      optionsType='button'
                      defaultValue={value}
                      radioButtonProps={{
                        shadow: 'none',
                        borderRadius: 'none',
                      }}
                      onChange={(value) => {
                        onChange(value);
                        updatePhysicalAddress(
                          false,
                          value,
                          '',
                          defaultPhyAddr.clientContact,
                        );
                      }}
                      options={['Client', 'Third Party']}
                      applyBorderRadiusForStartEndButtons={true}
                      borderRadiusForStartEndButtons={5}
                    />
                  );
                }}
              />
              <FormErrorMessage>{errors?.target?.message}</FormErrorMessage>
              <FormHelperText>
                <Text colorScheme='secondary' fontSize='small'>
                  Note: A third Party Processing agreement must be in place
                  prior to delivery to a third party processor
                </Text>
              </FormHelperText>
            </GridItem>
          </Grid>
        </FormControl>
      </FormLayout>

      <FormLayout
        header={
          <Flex direction='row' alignItems='baseline' w={'full'}>
            <Text mr={4}>
              {upperCase('Physical Address for Output Delivery')}{' '}
            </Text>
            <ContactPicker
              inputContact={props.model?.contact}
              onContactSelected={(newClientContact) => {
                if (onOutputContactSelected) {
                  onOutputContactSelected();
                }
                contactSelected(newClientContact);
              }}
              contacts={props.clientAttributes?.contacts}
              contactType={
                destinationTarget === OutputTargetParty.ThirdParty
                  ? 'ThirdParty'
                  : (a) =>
                      a.recordType?.toLowerCase() === 'client' &&
                      a.addressType?.toLowerCase() === 'shipping'
              }
            />
          </Flex>
        }
      >
        <Flex direction='column' justifyContent='start' w={'full'}>
          <FormControl isInvalid={!!errors.location}>
            <Grid templateColumns='repeat(6,1fr)' justifyContent={'end'}>
              <GridItem justifyContent={'end'} display={'flex'}>
                <FormLabel fontSize='sm' htmlFor='target' mb={0} mr={2}>
                  Output Folder :
                </FormLabel>
              </GridItem>
              <GridItem colSpan={5}>
                <Controller
                  control={control}
                  defaultValue={props.model?.location || null}
                  name='location'
                  render={({ value }) => <label>{value}</label>}
                />
                <FormErrorMessage>{errors?.location?.message}</FormErrorMessage>
              </GridItem>
            </Grid>
          </FormControl>
          <Grid templateColumns='repeat(1,1fr)' mt={6}>
            <GridItem colSpan={5} justifyContent={'left'} display={'flex'}>
              <ContactDetails
                isReadOnly={true}
                clearErrors={clearErrors}
                propName='contact.'
                control={formMethods.control}
                errors={errors?.contact}
                model={props.model?.contact}
              />
            </GridItem>
          </Grid>
        </Flex>
      </FormLayout>
    </form>
  );
};

export default forwardRef(OutputLocationTab);
